import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Camera, Upload, X, Crop } from 'lucide-react';
import ImageCropper from './ImageCropper';

const EnhancedProfilePictureUpload = () => {
  const { user, profile, updateProfile } = useAuth();
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(profile?.avatar_url || null);
  const [showCropper, setShowCropper] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadAvatar = async (blob: Blob) => {
    if (!user) {
      toast.error(t('please_sign_in', 'يرجى تسجيل الدخول', 'Please sign in to upload avatar'));
      return null;
    }

    const fileName = `${user.id}/avatar-${Date.now()}.png`;

    try {
      console.log('Starting upload for file:', fileName);
      
      // Delete existing avatar if it exists
      if (profile?.avatar_url) {
        const existingPath = profile.avatar_url.split('/').pop();
        if (existingPath && existingPath.includes(user.id)) {
          const existingFileName = profile.avatar_url.split('/').slice(-2).join('/');
          console.log('Deleting existing avatar:', existingFileName);
          await supabase.storage
            .from('avatars')
            .remove([existingFileName]);
        }
      }

      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(fileName, blob, {
          cacheControl: '3600',
          upsert: true,
          contentType: 'image/png'
        });

      if (error) {
        console.error('Upload error:', error);
        toast.error(t('upload_failed', 'فشل في رفع الصورة', `Failed to upload image: ${error.message}`));
        return null;
      }

      console.log('Upload successful:', data);

      // Get public URL with cache busting
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(data.path);

      // Add cache busting parameter to ensure fresh image load
      const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      
      console.log('Public URL generated with cache busting:', avatarUrl);
      return avatarUrl;
    } catch (error) {
      console.error('Upload exception:', error);
      toast.error(t('upload_failed', 'فشل في رفع الصورة', 'Failed to upload image'));
      return null;
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('invalid_file_type', 'نوع الملف غير صحيح', 'Please select an image file'));
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('file_too_large', 'الملف كبير جداً', 'File size must be less than 10MB'));
      return;
    }

    try {
      // Create preview URL for cropping
      const imageUrl = URL.createObjectURL(file);
      setOriginalImageUrl(imageUrl);
      setShowCropper(true);
    } catch (error) {
      console.error('Error creating image preview:', error);
      toast.error(t('preview_failed', 'فشل في معاينة الصورة', 'Failed to preview image'));
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setUploading(true);
    setShowCropper(false);

    try {
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(croppedBlob);

      // Upload cropped image
      const avatarUrl = await uploadAvatar(croppedBlob);
      
      if (avatarUrl) {
        // Update profile with new avatar URL
        const { error } = await updateProfile({
          avatar_url: avatarUrl
        });

        if (error) {
          console.error('Profile update error:', error);
          toast.error(t('update_failed', 'فشل في التحديث', 'Failed to update profile'));
        } else {
          toast.success(t('avatar_updated', 'تم تحديث الصورة الشخصية', 'Profile picture updated successfully'));
        }
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error(t('upload_failed', 'فشل في رفع الصورة', 'Failed to upload avatar'));
    } finally {
      setUploading(false);
      if (originalImageUrl) {
        URL.revokeObjectURL(originalImageUrl);
        setOriginalImageUrl(null);
      }
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user || !profile?.avatar_url) return;

    setUploading(true);

    try {
      // Delete from storage
      const fileName = profile.avatar_url.split('/').slice(-2).join('/');
      if (fileName.includes(user.id)) {
        console.log('Deleting avatar:', fileName);
        const { error: deleteError } = await supabase.storage
          .from('avatars')
          .remove([fileName]);
        
        if (deleteError) {
          console.error('Delete error:', deleteError);
        }
      }

      // Update profile
      const { error } = await updateProfile({
        avatar_url: null
      });

      if (error) {
        console.error('Profile update error:', error);
        toast.error(t('update_failed', 'فشل في التحديث', 'Failed to update profile'));
      } else {
        setImagePreview(null);
        toast.success(t('avatar_removed', 'تم حذف الصورة الشخصية', 'Profile picture removed'));
      }
    } catch (error) {
      console.error('Remove avatar error:', error);
      toast.error(t('remove_failed', 'فشل في حذف الصورة', 'Failed to remove avatar'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="relative group">
        <Avatar className="h-24 w-24">
          <AvatarImage 
            src={imagePreview || profile?.avatar_url || ''} 
            alt={profile?.full_name || t('profile_picture', 'الصورة الشخصية', 'Profile Picture')}
            onError={(e) => {
              console.error('Avatar image failed to load:', e.currentTarget.src);
              e.currentTarget.style.display = 'none';
            }}
          />
          <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-xl">
            {profile?.full_name 
              ? profile.full_name.trim().split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
              : '??'
            }
          </AvatarFallback>
        </Avatar>
        
        {/* Camera overlay on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
             onClick={() => fileInputRef.current?.click()}>
          <Camera className="h-6 w-6 text-white" />
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <Upload className="h-4 w-4 mr-2" />
          {uploading ? t('uploading', 'يتم الرفع...', 'Uploading...') : t('upload_photo', 'رفع صورة', 'Upload Photo')}
        </Button>
        
        {(imagePreview || profile?.avatar_url) && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRemoveAvatar}
            disabled={uploading}
          >
            <X className="h-4 w-4 mr-2" />
            {t('remove', 'حذف', 'Remove')}
          </Button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      <p className="text-xs text-muted-foreground text-center">
        {t('avatar_requirements', 'الحد الأقصى 10 ميجابايت. سيتم اقتصاص الصورة لتصبح دائرية.', 'Maximum 10MB. Image will be cropped to circular format.')}
      </p>

      {/* Image Cropper Modal */}
      {showCropper && originalImageUrl && (
        <ImageCropper
          isOpen={showCropper}
          onClose={() => {
            setShowCropper(false);
            if (originalImageUrl) {
              URL.revokeObjectURL(originalImageUrl);
              setOriginalImageUrl(null);
            }
          }}
          imageUrl={originalImageUrl}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
};

export default EnhancedProfilePictureUpload;
